import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  atHomeAlerts,
  atHomeCaseload,
  atHomeFleet,
  atHomeMonitoring,
  atHomePanels,
  atHomeReimbursements,
  atHomeRevenue,
  type AtHomeCaseloadMember,
} from '@aithora/shared';
import { usePageTab } from '../../shell/Shell';
import { sx } from '../../ui/designStyles';
import { useReadOnly, useReadOnlyReason } from '../../ui/ReadOnly';
import { useApi } from '../overview/common';
import { AdminDrawer } from './AdminDrawer';
import { MemberHome } from './MemberHome';
import { OpAlerts } from './OpAlerts';
import { OpDevices } from './OpDevices';
import { MemberFilter, OpMembers, type MemFilter } from './OpMembers';
import { OpMonitoring } from './OpMonitoring';
import { OpReimbursements } from './OpReimbursements';
import { OpRevenue, revenueOf } from './OpRevenue';
import { D, S } from './ui';

// The Operator's Aithora Home (design T11642-12340): the caseload, not one household.
// Members read the real GET /athome/caseload; the columns and tabs it does not carry
// yet (readiness, tier, alerts, account, devices, revenue, monitoring, reimbursements)
// read PROPOSED routes and draw the design's empty state until they are served.
// "View as" opens one member's Home, chosen from the caseload, read only.

export type OpTab = 'caseload' | 'fleet' | 'money' | 'ledger' | 'rebate' | 'alerts';

export function useOperatorData() {
  const caseload = useApi((c, signal) => atHomeCaseload(c, { signal }), []);
  const panels = useApi((c, signal) => atHomePanels(c, { signal }), []);
  const alerts = useApi((c, signal) => atHomeAlerts(c, { signal }), []);
  const fleet = useApi((c, signal) => atHomeFleet(c, { signal }), []);
  const revenue = useApi((c, signal) => atHomeRevenue(c, { signal }), []);
  const monitoring = useApi((c, signal) => atHomeMonitoring(c, { signal }), []);
  const rebate = useApi((c, signal) => atHomeReimbursements(c, { signal }), []);
  return { caseload, panels, alerts, fleet, revenue, monitoring, rebate };
}
export type OperatorData = ReturnType<typeof useOperatorData>;

/** What every Operator tab gets. */
export interface OpCtx {
  o: OperatorData;
  readOnly: boolean;
  reason: string | null;
  viewAs: (m: { residentId: string; name: string }) => void;
  manage: (residentId: string) => void;
  goTab: (t: OpTab) => void;
  memFilter: MemFilter;
  setMemFilter: (f: MemFilter) => void;
  /** Alerts tab shows only the unowned alerts past 15 minutes (from the header's count). */
  lateOnly: boolean;
  setLateOnly: (on: boolean) => void;
}

const TAB_LABEL: Record<OpTab, string> = { caseload: 'Members', fleet: 'Devices', money: 'Revenue', ledger: 'Monitoring', rebate: 'Reimbursements', alerts: 'Alerts' };
const TAB_ORDER: OpTab[] = ['caseload', 'fleet', 'money', 'ledger', 'rebate', 'alerts'];
const opTabStyle = (on: boolean, red: boolean) =>
  'display:inline-flex;align-items:center;height:28px;padding:0 12px;border-radius:7px;font-size:14px;font-weight:700;white-space:nowrap;text-decoration:none;' +
  (on ? 'color:#FCFAF4;background:#0F6B63;' : red ? 'color:#B3261E;background:rgba(179,38,30,.10);' : 'color:#3C454F;background:rgba(34,39,46,.07);');
const DASH = '–';

/** Unowned alerts older than 15 minutes (the design's unownedLate). */
export function unownedLate(o: OperatorData, now = Date.now()): number {
  return (o.alerts.data?.alerts ?? []).filter((a) => !a.owner && now - Date.parse(a.openedAt) > 15 * 60_000).length;
}

export function OperatorHome() {
  const o = useOperatorData();
  const readOnly = useReadOnly();
  const roReason = useReadOnlyReason();
  const [tab, setTab] = useState<OpTab>('caseload');
  const [admin, setAdmin] = useState<string | null>(null);
  const [memFilter, setMemFilter] = useState<MemFilter>('all');
  const [lateOnly, setLateOnly] = useState(false);
  const [params, setParams] = useSearchParams();
  const [viewing, setViewing] = useState<{ residentId: string; name: string } | null>(null);
  usePageTab(viewing ? 'View as member' : TAB_LABEL[tab]);

  // /home?member=<residentId> (from Atlas's quiet list) opens that member's Home,
  // only when the server's own caseload has them.
  const wanted = params.get('member');
  const members = o.caseload.data?.members;
  useEffect(() => {
    if (!wanted || !members) return;
    const m = members.find((x) => x.residentId === wanted);
    if (m) setViewing({ residentId: m.residentId, name: m.name });
  }, [wanted, members]);

  const stopViewing = () => {
    setViewing(null);
    if (params.has('member')) {
      params.delete('member');
      setParams(params, { replace: true });
    }
  };

  const ctx: OpCtx = {
    o,
    readOnly,
    reason: roReason,
    viewAs: (m) => {
      setAdmin(null);
      setViewing(m);
    },
    manage: setAdmin,
    goTab: setTab,
    memFilter,
    setMemFilter,
    lateOnly,
    setLateOnly,
  };

  const stats = useMemo(() => {
    const list: AtHomeCaseloadMember[] = o.caseload.data?.members ?? [];
    const al = o.alerts.data;
    const late = unownedLate(o);
    const silent = list.filter((m) => m.devicesReporting < m.devicesLinked).length;
    const mrr = o.revenue.data ? revenueOf(o.revenue.data).total : null;
    return [
      { k: 'members', v: o.caseload.data ? String(list.length) : DASH, color: '#22272E', tab: 'caseload' as OpTab },
      { k: 'open alerts', v: al ? String(al.alerts.length) : DASH, color: al?.alerts.length ? '#22272E' : '#54606C', tab: 'alerts' as OpTab },
      // A filter when there is something to filter to; with none it is a figure to read.
      { k: 'unowned >15m', v: al ? String(late) : DASH, color: late ? '#B3261E' : '#22272E', tab: 'alerts' as OpTab, late: true, still: !late },
      { k: 'devices silent', v: o.caseload.data ? String(silent) : DASH, color: silent ? '#4A5866' : '#22272E', tab: 'fleet' as OpTab },
      { k: 'MRR · modeled', v: mrr == null ? DASH : `$${mrr.toLocaleString('en-US')}`, color: '#12503C', tab: 'money' as OpTab },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [o.caseload.data, o.alerts.data, o.revenue.data]);

  const counts: Record<OpTab, string> = {
    caseload: o.caseload.data ? String(o.caseload.data.members.length) : DASH,
    fleet: o.caseload.data ? String(o.caseload.data.members.reduce((n, m) => n + (m.devicesLinked - m.devicesReporting), 0)) : DASH,
    money: o.revenue.data ? String(o.revenue.data.members.length) : DASH,
    ledger: o.monitoring.data ? String(o.monitoring.data.ledgerToday) : DASH,
    rebate: o.rebate.data ? String(o.rebate.data.findings.length) : DASH,
    alerts: o.alerts.data ? String(o.alerts.data.alerts.length) : DASH,
  };

  if (viewing) {
    const reason = `Read only: you are viewing as ${viewing.name}. Nothing you press here is sent.`;
    return (
      <>
        <D css="display:flex;align-items:center;gap:10px;flex-wrap:wrap;padding:8px 12px;margin-bottom:10px;border-radius:9px;background:rgba(91,107,124,.12);border:1px solid rgba(91,107,124,.3);" role="status" testId="viewas-member-bar">
          <S css="font-size:14px;font-weight:700;letter-spacing:.08em;color:#5B6B7C;">VIEWING AS MEMBER</S>
          <S css="flex:1 1 160px;min-width:0;font-size:14px;color:#3C454F;">{`This is exactly what ${viewing.name} sees. Read only: nothing you press here is sent, and your own role is unchanged.`}</S>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              stopViewing();
            }}
            style={sx('display:inline-flex;align-items:center;height:30px;padding:0 12px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;color:#FCFAF4;background:#5B6B7C;')}
          >
            Back to caseload
          </a>
        </D>
        <MemberHome key={viewing.residentId} personId={viewing.residentId} orgId={null} readOnly reason={reason} />
      </>
    );
  }

  let body: ReactNode = null;
  if (tab === 'caseload') body = <OpMembers c={ctx} />;
  else if (tab === 'alerts') body = <OpAlerts c={ctx} />;
  else if (tab === 'fleet') body = <OpDevices c={ctx} />;
  else if (tab === 'money') body = <OpRevenue c={ctx} />;
  else if (tab === 'ledger') body = <OpMonitoring c={ctx} />;
  else body = <OpReimbursements c={ctx} />;

  const list = o.caseload.data?.members ?? [];
  const region = o.caseload.data?.region;
  const late = unownedLate(o);
  return (
    <>
      {admin && <AdminDrawer c={ctx} residentId={admin} onClose={() => setAdmin(null)} />}
      <div style={sx('display:flex;flex-direction:column;min-width:0;gap:0;flex:1 1 auto;min-height:0;height:100%;overflow:hidden;')} data-testid="operator-home">
        <D css="flex:0 0 auto;display:flex;align-items:flex-end;gap:10px 22px;flex-wrap:wrap;padding-bottom:8px;border-bottom:1px solid rgba(34,39,46,.14);">
          <S css="flex:1 1 240px;min-width:0;">
            <S css="display:block;font-size:16px;font-weight:700;letter-spacing:-.012em;color:#22272E;">Aithora Home {'·'} members</S>
            <S css="display:block;font-size:14px;color:#54606C;margin-top:2px;">
              {o.caseload.data
                ? [`${list.length} ${list.length === 1 ? 'member' : 'members'} at home`, region, o.alerts.data ? `${o.alerts.data.alerts.length} open alerts` : null, o.caseload.data.note].filter(Boolean).join(' · ')
                : o.caseload.error
                  ? `The caseload did not load: ${o.caseload.error.message}`
                  : ''}
            </S>
          </S>
          <S css="display:flex;align-items:baseline;gap:18px;flex-wrap:wrap;min-width:0;flex:0 1 auto;">
            {stats.map((s) => {
              const face = (
                <>
                  <S css={'display:block;font-size:22px;font-weight:700;letter-spacing:-.025em;line-height:1;font-variant-numeric:tabular-nums;color:' + s.color + ';'}>{s.v}</S>
                  <S css="display:block;font-size:14px;font-weight:600;color:#54606C;margin-top:2px;">{s.k}</S>
                </>
              );
              if ('still' in s && s.still) return <span key={s.k} data-testid="op-stat-static" style={sx('display:block;text-align:right;color:inherit;')}>{face}</span>;
              return (
                <a
                  key={s.k}
                  href="#"
                  aria-current={tab === s.tab && (!('late' in s) || lateOnly) ? 'true' : undefined}
                  onClick={(e) => {
                    e.preventDefault();
                    setLateOnly('late' in s);
                    setTab(s.tab);
                  }}
                  style={sx('display:block;text-align:right;text-decoration:none;color:inherit;')}
                >
                  {face}
                </a>
              );
            })}
          </S>
        </D>
        <S css="flex:0 0 auto;display:flex;align-items:center;gap:6px;flex-wrap:wrap;padding:6px 0;min-width:0;">
          {TAB_ORDER.map((t) => (
            <a
              key={t}
              href="#"
              role="tab"
              aria-selected={tab === t}
              onClick={(e) => {
                e.preventDefault();
                setTab(t);
              }}
              style={sx(opTabStyle(tab === t, t === 'alerts' && late > 0))}
            >
              {`${TAB_LABEL[t]} ${counts[t]}`}
            </a>
          ))}
          <S css="flex:1 1 20px;min-width:0;" />
          {tab === 'caseload' && <MemberFilter c={ctx} />}
        </S>
        <div style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;overflow-x:hidden;min-width:0;display:block;padding-right:3px;')} role="tabpanel">
          {body}
        </div>
      </div>
    </>
  );
}
